import { ChangeDetectionStrategy, Component } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { GridOptions } from 'ag-grid-community';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { map, publishReplay, refCount, switchMap } from 'rxjs/operators';
import { CovanceGridColumn } from '../../../covance-ag-grid';
import { CovanceFilter, FilterFieldType, FilterFieldValue, SerializedFilter } from '../../../covance-filter';
import { documentTypeColumn, protocolNameColumn, projectNumberColumn, siteNumberColumn, descriptionColumn, notifyTypeColumn, emailDateTimeColumn, emailSentToColumn } from '../../../shared';
import { AdminEmailNotificationAlertReport } from '../report-types/admin-activity-report-email-notification-alert';
import { AdminEmailAlertsStorage } from './admin-email-alerts-storage';

@Component({
    selector: 'admin-email-notification-alerts-reports',
    template: require('./admin-email-notification-alerts-reports.component.html'),
    styles: [require('./admin-email-notification-alerts-reports.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminEmailNotificationAlertsComponent {

    public emailNotificationAlertsFilterDefinitions: FilterFieldValue[] = [
        {
            def: {
                title: 'Protocol',
                queryParam: 'protocol',
                isServerSideFilter: true,
                type: FilterFieldType.MultiSelect
            }
        },
        {
            def: {
                title: 'Project Number',
                queryParam: 'project',
                isServerSideFilter: true,
                type: FilterFieldType.MultiSelect
            }
        },
        {
            def: {
                title: 'Document Type',
                queryParam: 'documentType',
                isServerSideFilter: true,
                type: FilterFieldType.MultiSelect
            }
        },
        {
            def: {
                title: 'Sent Time',
                queryParam: 'sentDate',
                isServerSideFilter: true,
                type: FilterFieldType.DateRange
            }
        }
    ];

    public reports$: Observable<AdminEmailNotificationAlertReport[]>;
    public columnDefs: CovanceGridColumn[];
    public filterFields$: Observable<FilterFieldValue[]>;
    public gridOptions: GridOptions;
    public usersControl = new FormControl(null, Validators.required);
    public quickSearchSubject$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
    public filtersExportData: Observable<SerializedFilter[]>;

    constructor(
        private emailAlertsStorage: AdminEmailAlertsStorage,
        private readonly filterService: CovanceFilter,
    ) {
        this.reports$ = of([]);
        this.usersControl = new FormControl(null, Validators.required);
        this.columnDefs = this.getColumnDefs();

        this.usersControl.valueChanges.subscribe(value => {
            if (value) {
                this.filterFields$ = this.emailAlertsStorage.loadEmailNotificationAlertsFilterItems(this.usersControl.value ? this.usersControl.value[0].userPk : 1).pipe(
                    map(filterItems => {
                        const filterDefs: FilterFieldValue[] = this.emailNotificationAlertsFilterDefinitions;
                        this.filterService.setFiltersItems(filterItems, filterDefs);
                        return filterDefs;
                    }),
                    publishReplay(1),
                    refCount());

                const filterFlow$ = this.filterFields$
                    .pipe(
                        switchMap(fields => this.filterService.listen(fields).pipe(map(() => fields))),
                        publishReplay(1),
                        refCount());

                this.reports$ = filterFlow$
                    .pipe(
                        switchMap((fields) => {
                            return this.emailAlertsStorage.getReports(this.usersControl.value[0].userPk, fields);
                        }),
                        publishReplay(1),
                        refCount()
                    ) as Observable<AdminEmailNotificationAlertReport[]>;

                this.filtersExportData = combineLatest([filterFlow$, this.quickSearchSubject$])
                    .pipe(
                        map(([fieldValues, quickSearchValue]) => {
                            const filtersExportData = this.filterService.serializeFieldValues(fieldValues);
                            filtersExportData.unshift({ title: 'UserID', value: value[0].userPk });
                            filtersExportData.unshift({ title: 'Search', value: quickSearchValue });
                            return filtersExportData;
                        }),
                    );
            }

        });
    }

    public onQuickSearchChanged(value: string) {
        this.quickSearchSubject$.next(value);
    }

    private getColumnDefs(): CovanceGridColumn[] {
        const columns: CovanceGridColumn[] = [
            documentTypeColumn,
            protocolNameColumn,
            projectNumberColumn,
            siteNumberColumn,
            descriptionColumn,
            notifyTypeColumn,
            emailDateTimeColumn,
            emailSentToColumn,
        ];
        return columns;
    }
   
}